import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  CardHeader,
  Text,
  makeStyles,
  tokens,
  Badge,
} from '@fluentui/react-components';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  AreaChart,
  Area,
  Legend,
} from 'recharts';
import { useApi } from '../auth/useApi.ts';
import { useI18n } from '../i18n/context.tsx';

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  stat: {
    padding: '20px',
  },
  statValue: {
    fontSize: tokens.fontSizeHero900,
    fontWeight: tokens.fontWeightBold,
    lineHeight: '1',
  },
  statLabel: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    marginTop: '8px',
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
    gap: '16px',
  },
  chart: {
    padding: '20px',
    height: '360px',
  },
});

interface Sample {
  ts: number;
  total: number;
  errors: number;
  avgLatencyMs: number;
  p95LatencyMs: number;
  perKey: Record<string, number>;
}

// Deterministic hue per key label — keeps stack colors stable across refreshes.
function colorFor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) & 0xffff;
  return `hsl(${h % 360}, 65%, 55%)`;
}

export function DashboardPage() {
  const styles = useStyles();
  const { openStream } = useApi();
  const { t } = useI18n();
  const [samples, setSamples] = useState<Sample[]>([]);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const es = await openStream('/admin/stream/traffic');
      if (cancelled) { es.close(); return; }
      esRef.current = es;
      es.onmessage = (ev) => {
        try {
          const sample = JSON.parse(ev.data) as Sample;
          setSamples((prev) => [...prev.slice(-299), sample]);
        } catch {}
      };
    })();
    return () => {
      cancelled = true;
      esRef.current?.close();
    };
  }, [openStream]);

  const latest = samples[samples.length - 1];
  const errorRate = latest && latest.total > 0 ? (latest.errors / latest.total) * 100 : 0;

  // Gather the full set of key labels seen in the visible window so the
  // stacked area chart keeps a stable set of series even when a given key
  // drops to zero in a sample.
  const keyLabels = useMemo(() => {
    const set = new Set<string>();
    for (const s of samples) {
      for (const k of Object.keys(s.perKey ?? {})) set.add(k);
    }
    return Array.from(set);
  }, [samples]);

  const chartData = useMemo(
    () =>
      samples.map((s) => {
        const row: Record<string, number | string> = {
          t: new Date(s.ts).toLocaleTimeString(),
          total: s.total,
          errors: s.errors,
          avgLatencyMs: s.avgLatencyMs,
          p95LatencyMs: s.p95LatencyMs ?? 0,
        };
        for (const k of keyLabels) row[k] = s.perKey?.[k] ?? 0;
        return row;
      }),
    [samples, keyLabels],
  );

  return (
    <div>
      <div className={styles.grid}>
        <Card className={styles.stat}>
          <Text className={styles.statValue}>{latest?.total ?? 0}</Text>
          <Text className={styles.statLabel}>{t('dashboard', 'reqsPerSec')}</Text>
        </Card>
        <Card className={styles.stat}>
          <Text className={styles.statValue}>{latest?.avgLatencyMs ?? 0} ms</Text>
          <Text className={styles.statLabel}>{t('dashboard', 'avgLatency')}</Text>
        </Card>
        <Card className={styles.stat}>
          <Text className={styles.statValue}>{latest?.p95LatencyMs ?? 0} ms</Text>
          <Text className={styles.statLabel}>{t('dashboard', 'p95Latency')}</Text>
        </Card>
        <Card className={styles.stat}>
          <Text className={styles.statValue}>{errorRate.toFixed(1)}%</Text>
          <Text className={styles.statLabel}>{t('dashboard', 'errorRate')}</Text>
        </Card>
        <Card className={styles.stat}>
          <Text className={styles.statValue}>{Object.keys(latest?.perKey ?? {}).length}</Text>
          <Text className={styles.statLabel}>{t('dashboard', 'activeKeys')}</Text>
        </Card>
      </div>

      <div className={styles.chartsGrid}>
        <Card className={styles.chart}>
          <CardHeader
            header={<Text weight="semibold">{t('dashboard', 'liveTraffic')}</Text>}
            description={<Badge appearance="tint" color="informative">{t('dashboard', 'refresh1s')}</Badge>}
          />
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke={tokens.colorNeutralStroke3} strokeDasharray="3 3" />
              <XAxis dataKey="t" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="total"
                stroke={tokens.colorBrandForeground1}
                strokeWidth={2}
                dot={false}
                name={t('dashboard', 'legendTotal')}
                isAnimationActive={false}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="errors"
                stroke={tokens.colorPaletteRedForeground1}
                strokeWidth={2}
                dot={false}
                name={t('dashboard', 'legendErrors')}
                isAnimationActive={false}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="p95LatencyMs"
                stroke={tokens.colorPaletteYellowForeground1}
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={false}
                name={t('dashboard', 'p95Latency')}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className={styles.chart}>
          <CardHeader
            header={<Text weight="semibold">{t('dashboard', 'perKeyStack')}</Text>}
            description={<Badge appearance="tint" color="informative">{t('dashboard', 'refresh1s')}</Badge>}
          />
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid stroke={tokens.colorNeutralStroke3} strokeDasharray="3 3" />
              <XAxis dataKey="t" />
              <YAxis />
              <Tooltip />
              <Legend />
              {keyLabels.map((k) => (
                <Area
                  key={k}
                  type="monotone"
                  dataKey={k}
                  stackId="perkey"
                  stroke={colorFor(k)}
                  fill={colorFor(k)}
                  fillOpacity={0.7}
                  isAnimationActive={false}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
